import { Component } from '@verndale/core';
import GeneralTab from './generalTab';
import { animate, AnimationPlaybackControls, AnimationSequence } from 'framer-motion';
import { breakpoints } from '../../helpers/variables';

export interface TabItem {
  id: string;
  anchor?: string;
  isActive: boolean;
  setActive(isActive: boolean, force?: boolean): Promise<unknown> | void;
}

class Tabs extends Component {
  panels: TabItem[] = [];
  imagePanels: TabItem[] = [];
  prefix: string | undefined;
  isVertical: boolean = false;
  viewingText: string | undefined;

  constructor(el: HTMLElement) {
    super(el);
    this.isVertical = (this.dom.tabsContainer as HTMLElement).classList.contains('tabs--vertical');
    if (this.el.dataset.viewingText) this.viewingText = this.el.dataset.viewingText;
    this.buildTabs();
    this.addTabListeners();
  }

  setupDefaults() {
    if (this.el.dataset.prefix) this.prefix = this.el.dataset.prefix;

    this.dom = {
      tabsContainer: this.el.querySelector<HTMLElement>('.tabs'),
      panels: this.prefix
        ? this.el.querySelectorAll<HTMLElement>(`.tabs__${this.prefix}-panel`)
        : this.el.querySelectorAll<HTMLElement>('.tabs__panel'),
      imagePanels: this.prefix
        ? this.el.querySelectorAll<HTMLElement>(`.tabs__${this.prefix}-panel-image`)
        : this.el.querySelectorAll<HTMLElement>('.tabs__panel-image')
    };
  }

  buildTabs() {
    const panels = this.dom.panels as NodeListOf<HTMLDivElement>;
    const imagePanels = this.dom.imagePanels as NodeListOf<HTMLDivElement>;
    const tabsContainer = this.dom.tabsContainer as HTMLDivElement;
    tabsContainer.innerHTML = '';
    const mobileTabs = document.createElement('div');
    mobileTabs.classList.add('tabs__mobile');
    const desktopTabs = document.createElement('div');
    desktopTabs.classList.add('tabs__desktop');

    let tabsContent = '';
    let dropdownContent = '';

    panels.forEach((item, index) => {
      const uuid = item.id;

      item.setAttribute('aria-labelledby', `tab-${uuid}`);
      if (index) {
        item.classList.add('tabs__item--hidden');
      }

      tabsContent += `
        <button class="tab" type="button" role="tab" id="tab-${uuid}" aria-controls="${uuid}" ${
          index ? ' tabindex="-1"' : ''
        }>
        ${item.dataset.title}
        </button>
      `;

      dropdownContent += `<option value="${uuid}">${
        this.viewingText ? this.viewingText : ''
      }${item.dataset.title}</option>`;
      this.panels.push(new GeneralTab(item, !index));
      if (imagePanels[index]) this.imagePanels.push(new GeneralTab(imagePanels[index], !index));
    });

    desktopTabs.innerHTML = tabsContent;
    this.dom.tabs = desktopTabs.querySelectorAll('button');

    this.dom.dropdown = document.createElement('select');
    this.dom.dropdown.classList.add('tabs__dropdown');
    this.dom.dropdown.classList.add('dropdown');
    this.dom.dropdown.innerHTML = dropdownContent;

    mobileTabs.append(this.dom.dropdown);
    tabsContainer.append(desktopTabs);
    tabsContainer.append(mobileTabs);

    // check if there is a hash in the url and set the active panel
    const hash = window.location.hash;
    let activeId;
    if (hash) {
      activeId = this.panels.find(panel => panel.anchor === hash.replace('#', ''))?.id;
      this.el.scrollIntoView({ behavior: 'smooth' });
    }

    this.setSelectedPanel(activeId || this.panels[0].id, true);
  }

  addTabListeners() {
    (this.dom.tabs as NodeList).forEach(tab => {
      tab.addEventListener('click', this.handleTabClick.bind(this));
      tab.addEventListener('keydown', this.handleTabKeyDown.bind(this));
    });
    (this.dom.dropdown as HTMLSelectElement).addEventListener(
      'change',
      this.handleDropdownChange.bind(this)
    );

    window.addEventListener('hashchange', this.handleHashChange.bind(this));
  }

  handleTabClick(e: Event) {
    const target = e.currentTarget as HTMLButtonElement;
    const activeId = target.getAttribute('aria-controls');
    (this.dom.tabsContainer as HTMLElement).scrollTo({
      left: target.offsetLeft,
      behavior: 'smooth'
    });

    this.setSelectedPanel(`${activeId}`);
  }

  handleTabKeyDown(e: Event) {
    if (!(e instanceof KeyboardEvent)) return;

    const target = e.currentTarget as HTMLButtonElement;
    const tabs = this.dom.tabs as NodeListOf<HTMLButtonElement>;
    let flag = false;

    switch (e.key) {
      case 'ArrowLeft':
        if (target.previousElementSibling) {
          (target.previousElementSibling as HTMLButtonElement).focus();
        } else {
          (tabs[tabs.length - 1] as HTMLButtonElement).focus();
        }
        flag = true;
        break;

      case 'ArrowRight':
        if (target.nextElementSibling) {
          (target.nextElementSibling as HTMLButtonElement).focus();
        } else {
          (tabs[0] as HTMLButtonElement).focus();
        }
        flag = true;
        break;

      case 'Home':
        (tabs[0] as HTMLButtonElement).focus();
        flag = true;
        break;

      case 'End':
        (tabs[tabs.length - 1] as HTMLButtonElement).focus();
        flag = true;
        break;

      default:
        break;
    }

    if (flag) {
      e.stopPropagation();
      e.preventDefault();
    }
  }

  handleHashChange() {
    const hash = window.location.hash;
    const activeId = this.panels.find(panel => panel.anchor === hash.replace('#', ''))?.id;

    if (activeId) {
      this.setSelectedPanel(activeId, false);
      this.el.scrollIntoView({ behavior: 'smooth' });
    }
  }

  handleDropdownChange(e: Event) {
    const target = e.target as HTMLSelectElement;
    const activeId = target.value;

    this.setSelectedPanel(activeId);
  }

  async setSelectedPanel(id: string, setUrl = true) {
    const tabs = [...(this.dom.tabs as NodeListOf<HTMLButtonElement>)];

    const currentPanel = this.panels.find(panel => panel.isActive) as GeneralTab;
    const selectedPanel = this.panels.find(panel => panel.id === id) as GeneralTab;
    const currentImagePanel = this.imagePanels.find(panel => panel.isActive) as GeneralTab;
    const selectedImagePanel = this.imagePanels.find(
      panel => panel.id === `${id}--image`
    ) as GeneralTab;

    // Deselect active tab
    const currentTab = tabs.find(tab => tab.getAttribute('aria-selected') === 'true');
    currentTab?.setAttribute('aria-selected', 'false');
    currentTab?.setAttribute('tabindex', '-1');

    // Select new tab
    const selectedTab = tabs.find(tab => tab.id === `tab-${id}`);
    selectedTab?.setAttribute('aria-selected', 'true');
    selectedTab?.removeAttribute('tabindex');

    // Update the url with the fata-anchor value
    if (setUrl) {
      const newUrl = new URL(window.location.href);
      newUrl.hash = selectedPanel?.anchor || '';
      window.history.pushState({}, '', newUrl.toString());
    }

    if (this.isVertical) {
      (this.el.querySelector('.tabs__desktop') as HTMLElement).style.setProperty(
        '--inset-block-start',
        `${(selectedTab as HTMLElement).offsetTop}px`
      );
      (this.el.querySelector('.tabs__desktop') as HTMLElement).style.setProperty(
        '--block-size',
        `${(selectedTab as HTMLElement).offsetHeight}px`
      );
    } else {
      (this.el.querySelector('.tabs__desktop') as HTMLElement).style.setProperty(
        '--inset-inline-start',
        `${(selectedTab as HTMLElement).offsetLeft}px`
      );
      (this.el.querySelector('.tabs__desktop') as HTMLElement).style.setProperty(
        '--inline-size',
        `${(selectedTab as HTMLElement).offsetWidth}px`
      );
    }

    // if (this.panelInTransition) {
    //   // Check if previous panel in transition
    //   await this.panelInTransition?.setActive(false);
    // }

    const carouselSlides = document
      .getElementById(currentPanel.id)
      ?.querySelectorAll('.swiper-slide');

    if (carouselSlides) {
      if (currentPanel === selectedPanel) {
        this.updatePanelStatus(currentPanel, currentImagePanel, selectedPanel, selectedImagePanel);
        this.animateCarouselSlides(carouselSlides);
      } else {
        const selectedCarouselSlides = document
          .getElementById(selectedPanel.id)
          ?.querySelectorAll('.swiper-slide');

        if (selectedCarouselSlides) {
          this.animateCarouselSlides(carouselSlides, 'out').then(() => {
            this.updatePanelStatus(
              currentPanel,
              currentImagePanel,
              selectedPanel,
              selectedImagePanel
            );
            this.animateCarouselSlides(selectedCarouselSlides);
          });
        }
      }
    } else {
      this.updatePanelStatus(currentPanel, currentImagePanel, selectedPanel, selectedImagePanel);
    }
  }

  updatePanelStatus(
    currentPanel: GeneralTab,
    currentImagePanel: GeneralTab,
    selectedPanel: GeneralTab,
    selectedImagePanel: GeneralTab
  ) {
    currentPanel?.setActive(false);
    currentImagePanel?.setActive(false);
    selectedPanel?.setActive(true);
    selectedImagePanel?.setActive(true);
  }

  animateCarouselSlides(
    carouselSlides: NodeListOf<Element>,
    direction: string = 'in'
  ): AnimationPlaybackControls {
    const sequence: AnimationSequence = [];
    const tabletMQ = window.matchMedia(`(max-width:${breakpoints.tabletLandscape - 1}px)`);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const maxVisibleSlides = tabletMQ.matches ? 1 : 4;

    carouselSlides.forEach((slide, idx) => {
      const yDuration = idx < maxVisibleSlides && !reducedMotion.matches ? 0.8 : 0;
      const opDuration = idx < maxVisibleSlides && !reducedMotion.matches ? 0.4 : 0;

      sequence.push([
        slide,
        {
          y: direction === 'in' ? [-50, 0] : [0, 50],
          opacity: direction === 'in' ? [0, 1] : [1, 0]
        },
        {
          ease: direction === 'in' ? 'easeOut' : 'easeIn',
          duration: yDuration,
          opacity: { duration: opDuration },
          at: '-0.7'
        }
      ]);
    });

    const animation: AnimationPlaybackControls = animate(sequence);

    return animation;
  }
}

export default Tabs;
